import { Suspense, lazy } from 'react';
import type { ComponentType } from 'react';
import { Navigate, createBrowserRouter, type RouteObject } from 'react-router';

import { AppLayout } from '@/components/AppLayout';
import { FullScreenSpin } from '@/components/FullScreenSpin';
import { LoginPage } from '@/features/auth/LoginPage';
import { RequireAuth } from '@/features/auth/RequireAuth';
import { CrewLayout } from '@/features/doctor/CrewLayout';
import { CrewOffline } from '@/features/doctor/CrewOffline';
import { crewPath } from '@/features/doctor/crewPaths';
import { OperatorShiftGate } from '@/features/shifts/OperatorShiftGate';
import { PlaceholderPage } from '@/features/placeholder/PlaceholderPage';
import { SETUP_PATH, SetupGate } from '@/features/setup/SetupGate';

import { DOCTOR_HOME, NAV_ITEMS, NO_ACCESS_PATH } from './navigation';
import { HomeRedirect, NoAccess, NotFound, RouteError } from './routeElements';

// Real sections are split into their own chunks; the rest are placeholders for now.
const SECTIONS: Record<string, ComponentType> = {
  '/calls': lazy(() => import('@/features/calls/CallsSection')),
  '/shifts': lazy(() => import('@/features/shifts/ShiftsSection')),
  '/fleet-map': lazy(() => import('@/features/fleet/FleetMapPage')),
  '/journals': lazy(() => import('@/features/journals/JournalsPage')),
  '/dictionaries': lazy(() => import('@/features/dictionaries/DictionariesSection')),
  '/patients': lazy(() => import('@/features/patients/PatientsSection')),
  '/settings': lazy(() => import('@/features/settings/SettingsPage')),
  '/incidents': lazy(() => import('@/features/incidents/IncidentsPage')),
  '/complaints': lazy(() => import('@/features/complaints/ComplaintsPage')),
  '/audit': lazy(() => import('@/features/audit/AuditPage')),
  '/vendor': lazy(() => import('@/features/vendor/VendorPage')),
  '/notifications': lazy(() => import('@/features/notifications/NotificationsPage')),
  '/reports': lazy(() => import('@/features/reports/ReportsPage')),
  '/referrers': lazy(() => import('@/features/referrers/ReferrersSection')),
};

// The dispatcher desk takes the whole screen - three columns at 1920 px leave no room
// for the side menu (STAGE_4 4.2) - so it lives outside the layout, with its own header.
const DESK_PATH = '/dispatch';
const DESK: Record<'Page', ComponentType> = {
  Page: lazy(() => import('@/features/dispatch/DispatchPage')),
};
const DESK_ROLES = NAV_ITEMS.find((item) => item.path === DESK_PATH)?.roles ?? [];
// Whoever has a section in the web workspace gets its layout: read off the map, so a
// role added there cannot be left out here.
const WEB_ROLES = [
  ...new Set(NAV_ITEMS.filter((item) => item.path !== DESK_PATH).flatMap((item) => item.roles)),
];

// The crew app (STAGE_5 5.1): its own frame, four tabs, a chunk of its own.
const CREW: Record<'calls' | 'shift' | 'expense' | 'history', ComponentType> = {
  calls: lazy(() => import('@/features/doctor/CrewCallsPage')),
  shift: lazy(() => import('@/features/doctor/CrewShiftPage')),
  expense: lazy(() => import('@/features/doctor/CrewExpensePage')),
  history: lazy(() => import('@/features/doctor/CrewHistoryPage')),
};

// The call card by steps (STAGE_5 5.4), a chunk of its own inside the crew app.
const CREW_REPORT: Record<'Page', ComponentType<{ readOnly?: boolean }>> = {
  Page: lazy(() => import('@/features/doctor/report/ReportWizard')),
};

// A call of "History", read-only (TZ 8).
const CREW_HISTORY_CALL: Record<'Page', ComponentType> = {
  Page: lazy(() => import('@/features/doctor/CrewHistoryCallPage')),
};

// The money taken for a call (STAGE_5 5.5).
const CREW_PAYMENT: Record<'Page', ComponentType> = {
  Page: lazy(() => import('@/features/doctor/CrewPaymentPage')),
};

function sectionElement(path: string, label: string, stage: number) {
  const Section = SECTIONS[path];
  return Section ? <Section /> : <PlaceholderPage title={label} stage={stage} />;
}

// The first run's wizard (STAGE_7 7.9): the admin's, reached from the settings and
// offered at sign-in on a new installation; not a section of the menu.
const SETUP: Record<'Page', ComponentType> = {
  Page: lazy(() => import('@/features/setup/SetupWizard')),
};

// The patient's rating page (STAGE_6 6.5): public, opened from an SMS on a phone, so it
// is its own small chunk and knows nothing of accounts.
const RATE: Record<'Page', ComponentType> = {
  Page: lazy(() => import('@/features/rating/RatePage')),
};

/** The app's routes; exported so tests can walk them in a memory router. */
export const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage />, errorElement: <RouteError /> },
  { path: NO_ACCESS_PATH, element: <NoAccess />, errorElement: <RouteError /> },
  {
    path: '/rate/:token',
    element: (
      <Suspense fallback={<FullScreenSpin />}>
        <RATE.Page />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: `${DESK_PATH}/*`,
    element: (
      <RequireAuth roles={DESK_ROLES}>
        <OperatorShiftGate>
          <Suspense fallback={<FullScreenSpin />}>
            <DESK.Page />
          </Suspense>
        </OperatorShiftGate>
      </RequireAuth>
    ),
    errorElement: <RouteError />,
  },
  {
    path: DOCTOR_HOME,
    element: (
      // In the car the network comes and goes: no connection at start is a screen of
      // the crew app, which tries again by itself.
      <RequireAuth roles={['doctor']} offline={<CrewOffline />}>
        <CrewLayout />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      // Absolute: a relative path in a splat route counts from the splat and loops.
      { index: true, element: <Navigate to={crewPath('calls')} replace /> },
      ...Object.entries(CREW).map(([tab, Page]) => ({ path: tab, element: <Page /> })),
      { path: 'calls/:callId/report', element: <CREW_REPORT.Page /> },
      { path: 'calls/:callId/payment', element: <CREW_PAYMENT.Page /> },
      { path: 'history/:callId', element: <CREW_HISTORY_CALL.Page /> },
      { path: 'history/:callId/card', element: <CREW_REPORT.Page readOnly /> },
      { path: '*', element: <Navigate to={crewPath('calls')} replace /> },
    ],
  },
  {
    path: '/',
    element: (
      <RequireAuth roles={WEB_ROLES}>
        {/* An operator works inside a shift (TZ 7.6); an admin is not asked. */}
        <OperatorShiftGate>
          <SetupGate>
            <AppLayout />
          </SetupGate>
        </OperatorShiftGate>
      </RequireAuth>
    ),
    // A section that throws must not take the layout with it.
    errorElement: <RouteError />,
    children: [
      { index: true, element: <HomeRedirect /> },
      ...NAV_ITEMS.filter((item) => item.path !== DESK_PATH).map((item) => ({
        path: `${item.path.slice(1)}/*`,
        element: (
          <RequireAuth roles={item.roles}>
            {sectionElement(item.path, item.label, item.stage)}
          </RequireAuth>
        ),
        // Inside the layout: the menu keeps working while one section is broken.
        errorElement: <RouteError />,
      })),
      {
        path: SETUP_PATH.slice(1),
        element: (
          <RequireAuth roles={['admin']}>
            <SETUP.Page />
          </RequireAuth>
        ),
        errorElement: <RouteError />,
      },
      { path: '*', element: <NotFound /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
